import { defineStore } from 'pinia';
import { useTravelataPinia } from '@/shared/composables';
import { createUuid } from '@/shared/lib/createUuid';
import type { LeadBannerGoal, LeadBannerPlacement, LeadBannerTrackingContext } from './leadBanner';
import { getLeadBannerGoalParams } from '../lib/leadBannerGoals';
import { yandexTrackingService } from '../lib/YandexTrackingService';

export const LEAD_BANNER_TRACKING_STORE_ID = 'travelata/analytics/leadBanner';

interface LeadBannerSession {
  cardId: string;
  placement: LeadBannerPlacement;
  sent: Set<LeadBannerGoal>;
}

const defineLeadBannerTrackingStore = defineStore(LEAD_BANNER_TRACKING_STORE_ID, () => {
  const sessions = new Map<string, LeadBannerSession>();

  function getSession({ key, placement }: LeadBannerTrackingContext): LeadBannerSession {
    const existing = sessions.get(key);

    if (existing) return existing;

    const session: LeadBannerSession = { cardId: createUuid(), placement, sent: new Set() };
    sessions.set(key, session);

    return session;
  }

  function track(goal: LeadBannerGoal, context: LeadBannerTrackingContext): void {
    const session = getSession(context);

    if (session.sent.has(goal)) return;

    session.sent.add(goal);
    yandexTrackingService.setTrackingGoal(goal, 'other', getLeadBannerGoalParams(goal, context, session.cardId));
  }

  function reset(placement?: LeadBannerPlacement): void {
    if (!placement) {
      sessions.clear();
      return;
    }

    Array.from(sessions.entries())
      .filter(([, session]) => session.placement === placement)
      .forEach(([key]) => sessions.delete(key));
  }

  return { track, reset };
});

export interface LeadBannerTracking {
  track(goal: LeadBannerGoal, context: LeadBannerTrackingContext): void;
  reset(placement?: LeadBannerPlacement): void;
}

export function useLeadBannerTracking(): LeadBannerTracking {
  const store = defineLeadBannerTrackingStore(useTravelataPinia());

  return { track: store.track, reset: store.reset };
}
